/**
 * HesaCurriculumSnapshot — Section 4 fallback for HESA-verified courses.
 *
 * Renders when the editorial `curriculum` block is absent (all 27,521
 * HESA rows). Combines:
 *   * `course_meta.num_stages` / `honours` / `sandwich_placement` /
 *     `year_abroad_available` / `distance_learning` — structural summary.
 *   * `course_meta.learning_teaching_url` / `assessments_url` /
 *     `employment_url` — deep links to the university's own module
 *     lists, teaching approach, and assessment framework.
 *   * NSS themes t3 (Assessment & feedback) + t5 (Organisation &
 *     management) + t6 (Learning resources) — student ratings of the
 *     course-delivery aspects. We already show all NSS themes in
 *     Section 5, but surfacing the assessment/organisation ones here
 *     is directly relevant to the curriculum decision.
 */
import { Card } from "@/components/ui/card";
import { Ev } from "@/components/app/degree/DegreeDetailBits";
import { BookOpen, ExternalLink, ClipboardCheck } from "lucide-react";

export function HesaCurriculumSnapshot({ hesa }) {
  if (!hesa) return null;
  const meta = hesa.course_meta || {};
  const themes = hesa.nss_themes?.themes || {};

  const bullets = [];
  if (meta.num_stages) bullets.push(`${meta.num_stages} stage${meta.num_stages === 1 ? "" : "s"}`);
  if (meta.honours) bullets.push("Honours degree");
  if (meta.sandwich_placement) bullets.push("Sandwich placement");
  if (meta.year_abroad_available) bullets.push("Year abroad");
  if (meta.distance_learning) bullets.push("Distance learning");
  if (meta.nhs_funded) bullets.push("NHS-funded");

  const links = [
    { label: "Module list & teaching approach", url: meta.learning_teaching_url, key: "learning" },
    { label: "Assessment framework",             url: meta.assessments_url,      key: "assess" },
    { label: "Graduate outcomes",                url: meta.employment_url,       key: "employ" },
  ].filter((l) => l.url);

  const curriculumThemes = [
    { key: "t3", label: "Assessment & feedback",     score: themes.t3?.score },
    { key: "t5", label: "Organisation & management", score: themes.t5?.score },
    { key: "t6", label: "Learning resources",        score: themes.t6?.score },
  ].filter((t) => t.score != null);

  if (bullets.length === 0 && links.length === 0 && curriculumThemes.length === 0) return null;

  const themeColor = (s) =>
    s >= 85 ? "bg-emerald-600" :
    s >= 70 ? "bg-emerald-500" :
    s >= 55 ? "bg-amber-500" :
              "bg-red-500";

  return (
    <Card className="p-4 border-stone-200 bg-white" data-testid="hesa-curriculum-snapshot">
      <div className="label-uppercase !text-[10px] text-stone-500 mb-3">
        HESA course structure & student ratings
      </div>

      {bullets.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4" data-testid="curriculum-structure">
          {bullets.map((b) => (
            <span
              key={b}
              className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 bg-[#F5F1EA] px-3 py-1 text-[12px] text-stone-800"
            >
              <BookOpen className="h-3 w-3 text-stone-500" />
              {b}
            </span>
          ))}
        </div>
      )}

      {curriculumThemes.length > 0 && (
        <div className="mb-4" data-testid="curriculum-nss-themes">
          <div className="label-uppercase !text-[10px] text-stone-500 mb-2 flex items-center gap-1.5">
            <ClipboardCheck className="h-3 w-3" /> What current students say (NSS {hesa.nss_themes?.aggregation_year || "2024-25"})
          </div>
          <div className="space-y-1.5">
            {curriculumThemes.map((t) => (
              <div
                key={t.key}
                data-testid={`curr-theme-${t.key}`}
                className="grid grid-cols-[10rem_1fr_3rem] items-center gap-2 text-[12px]"
              >
                <div className="text-stone-700">{t.label}</div>
                <div className="relative h-3 bg-stone-100 rounded-sm overflow-hidden">
                  <div
                    className={`absolute inset-y-0 left-0 rounded-sm ${themeColor(t.score)}`}
                    style={{ width: `${t.score}%` }}
                  />
                </div>
                <div className="text-right font-mono tabular-nums text-stone-800">
                  {t.score}%
                </div>
              </div>
            ))}
          </div>
          {hesa.nss_themes?.sample_size && (
            <div className="text-[10px] font-mono text-stone-500 mt-1.5">
              n = {hesa.nss_themes.sample_size} · response rate {hesa.nss_themes.response_rate_pct || "—"}%
            </div>
          )}
        </div>
      )}

      {links.length > 0 && (
        <div data-testid="curriculum-links">
          <div className="label-uppercase !text-[10px] text-stone-500 mb-2">
            Read the official course pages
          </div>
          <div className="flex flex-col gap-1.5">
            {links.map((l) => (
              <a
                key={l.key}
                href={l.url}
                target="_blank"
                rel="noreferrer"
                data-testid={`curr-link-${l.key}`}
                className="inline-flex items-center gap-1.5 text-[12px] text-[#4C1D95] hover:underline"
              >
                <ExternalLink className="h-3 w-3" /> {l.label}
              </a>
            ))}
          </div>
        </div>
      )}

      <Ev ev={{
        source:       "HESA Discover Uni 2025/26 · CC-BY-4.0",
        confidence:   "verified",
        url:          "https://discoveruni.gov.uk/",
      }} />
    </Card>
  );
}

export default HesaCurriculumSnapshot;
